/**
 * CourseModulesSection Component
 *
 * For SELF_GUIDED_COURSE rituals - manages video course modules
 * with title, description, duration, and drag-and-drop reordering.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Disc, GripVertical, Plus, Trash2, Video } from "lucide-react"
import { useRef, useState } from "react"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib"

export interface CourseModule {
  id: string
  uploadId?: string
  video_url?: string
  title: string
  description: string
  duration: string
  order: number
  isFree: boolean
  videoFile?: File
  videoPreview?: string
}

interface CourseModulesSectionProps {
  modules: CourseModule[]
  onAddModule: () => void
  onRemoveModule: (id: string) => void
  onReorderModules: (modules: CourseModule[]) => void
  onUpdateModule: (
    id: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => void
  moduleErrors?: Record<string, { title?: string; description?: string; video?: string }>
  sectionError?: string | null
}

// Sortable Module Component
function SortableModule({
  module,
  index,
  onRemoveModule,
  onRemoveVideo,
  onUpdateModule,
  videoInputRef,
  currentModuleIdRef,
  errors,
}: {
  module: CourseModule
  index: number
  onRemoveModule: (id: string) => void
  onRemoveVideo: (id: string) => void
  onUpdateModule: (
    id: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => void
  videoInputRef: React.RefObject<HTMLInputElement | null>
  currentModuleIdRef: React.RefObject<string | null>
  errors?: { title?: string; description?: string; video?: string }
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: module.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-border rounded-lg p-4 bg-card space-y-4"
    >
      {/* Module Header - Drag handle and order number */}
      <div className="flex items-center gap-3">
        <GripVertical
          {...attributes}
          {...listeners}
          className="text-body flex-shrink-0 cursor-grab active:cursor-grabbing"
          size={20}
        />
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="font-semibold text-sm text-primary">{index + 1}</span>
        </div>
        <span className="font-semibold text-primary">Module {index + 1}</span>
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemoveModule(module.id)}
          className="h-8 w-8 text-brand-terracotta-bright hover:text-brand-terracotta-bright"
        >
          <Trash2 size={16} />
        </Button>
      </div>

      {/* Video Upload */}
      <div>
        <label className="text-sm uppercase text-primary mb-2 block">
          VIDEO <span className="text-primary">*</span>
        </label>
        {module.videoPreview ? (
          <div className="relative">
            <video
              src={module.videoPreview}
              className="w-full max-h-48 object-cover rounded-md"
              controls
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onRemoveVideo(module.id)}
              className="absolute top-2 right-2 h-8 w-8 bg-background/80 hover:bg-background"
            >
              <Trash2 size={16} className="text-brand-terracotta-bright" />
            </Button>
          </div>
        ) : (
          <div
            className={cn(
              "border-1 border-dashed rounded-md py-8 text-center cursor-pointer transition-colors",
              errors?.video
                ? "border-brand-terracotta-bright hover:border-brand-terracotta-bright"
                : "border-input hover:border-primary",
            )}
            onClick={() => {
              currentModuleIdRef.current = module.id
              videoInputRef.current?.click()
            }}
          >
            <Disc className="mx-auto mb-2 h-8 w-8 text-body" />
            <p className="text-sm text-body">Click to upload video</p>
          </div>
        )}
        {errors?.video && (
          <p className="text-xs text-brand-terracotta-bright mt-1">{errors.video}</p>
        )}
      </div>

      {/* Title */}
      <CustomInput
        value={module.title}
        onChange={e => onUpdateModule(module.id, "title", e.target.value)}
        id={`module-title-${module.id}`}
        label="MODULE TITLE"
        required
        placeholder="Introduction to Meditation"
        error={errors?.title}
        onClearError={() => {}}
      />

      {/* Description */}
      <div className="space-y-2">
        <label className="text-sm uppercase text-primary">
          DESCRIPTION <span className="text-primary">*</span>
        </label>
        <textarea
          value={module.description}
          onChange={e => onUpdateModule(module.id, "description", e.target.value)}
          rows={3}
          placeholder="Brief description of what this module covers..."
          className={cn(
            "w-full border-b border-input bg-transparent py-2 text-sm focus:border-primary focus:outline-none resize-none",
            errors?.description && "border-brand-terracotta-bright",
          )}
        />
        {errors?.description && (
          <p className="text-xs text-brand-terracotta-bright mt-1">{errors.description}</p>
        )}
      </div>
    </div>
  )
}

export function CourseModulesSection({
  modules,
  onAddModule,
  onRemoveModule,
  onReorderModules,
  onUpdateModule,
  moduleErrors,
  sectionError,
}: CourseModulesSectionProps) {
  const videoInputRef = useRef<HTMLInputElement>(null)
  const currentModuleIdRef = useRef<string | null>(null)
  const videoBlobUrlsRef = useRef<Map<string, string>>(new Map())

  // Setup dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Helper function to get video duration from file
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise(resolve => {
      const video = document.createElement("video")
      video.preload = "metadata"
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src)
        // Return duration in seconds (rounded)
        resolve(Math.round(video.duration))
      }
      video.onerror = () => {
        resolve(0) // Default to 0 if error
      }
      video.src = URL.createObjectURL(file)
    })
  }

  const handleVideoUpload = async (moduleId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Create preview URL
    const previewUrl = URL.createObjectURL(file)
    videoBlobUrlsRef.current.set(moduleId, previewUrl)

    // Get video duration in seconds
    const durationInSeconds = await getVideoDuration(file)

    // Update all fields at once to avoid stale state
    onUpdateModule(moduleId, {
      videoFile: file,
      videoPreview: previewUrl,
      duration: `${durationInSeconds} sec`,
    })

    // Reset input
    if (videoInputRef.current) {
      videoInputRef.current.value = ""
    }
  }

  const handleRemoveVideo = (moduleId: string) => {
    const previewUrl = videoBlobUrlsRef.current.get(moduleId)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      videoBlobUrlsRef.current.delete(moduleId)
    }
    onUpdateModule(moduleId, {
      videoFile: undefined,
      videoPreview: "",
      duration: "",
    })
  }

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = modules.findIndex(m => m.id === active.id)
    const newIndex = modules.findIndex(m => m.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    // Reorder modules
    const newModules = [...modules]
    const [removed] = newModules.splice(oldIndex, 1)
    newModules.splice(newIndex, 0, removed)

    // Update order values
    const reorderedModules = newModules.map((module, index) => ({
      ...module,
      order: index,
    }))

    onReorderModules(reorderedModules)
  }

  const cleanupVideoUrls = () => {
    for (const url of videoBlobUrlsRef.current.values()) {
      URL.revokeObjectURL(url)
    }
    videoBlobUrlsRef.current.clear()
  }

  // Cleanup on unmount
  useState(() => {
    return () => cleanupVideoUrls()
  })

  return (
    <div className="space-y-4">
      {/* Section Error Message */}
      {sectionError && <p className="text-sm text-brand-terracotta-bright">{sectionError}</p>}

      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={e => {
          if (currentModuleIdRef.current) {
            handleVideoUpload(currentModuleIdRef.current, e)
            currentModuleIdRef.current = null
          }
        }}
      />

      {modules.length === 0 ? (
        <div
          className="border-1 border-dashed border-primary rounded-md py-12 text-center cursor-pointer"
          onClick={onAddModule}
        >
          <Video className="mx-auto mb-2 h-10 w-10 text-primary" />
          <p className="text-sm text-body">Click to add your first course module</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={modules.map(m => m.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {modules.map((module, index) => (
                <SortableModule
                  key={module.id}
                  module={module}
                  index={index}
                  onRemoveModule={onRemoveModule}
                  onRemoveVideo={handleRemoveVideo}
                  onUpdateModule={onUpdateModule}
                  videoInputRef={videoInputRef}
                  currentModuleIdRef={currentModuleIdRef}
                  errors={moduleErrors?.[module.id]}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Module Button */}
      <Button
        type="button"
        variant="ghost"
        onClick={onAddModule}
        className="flex items-center gap-2 text-sm font-medium text-body w-full py-3 border border-dashed border-input rounded-lg hover:border-primary"
      >
        <Plus size={18} />
        Add Module
      </Button>
    </div>
  )
}
